import React, { useEffect, useState } from 'react';
import SalesModule from './Sales.container';
import NoUnitAssigned from '@/components/access/NoUnitAssigned';
import { getSalesProfile, SalesError } from '@/services/sales/profileService';
import { ShoppingCart } from 'lucide-react';

const ACCESS_CACHE_KEY = 'sales_access_state';

function readAccessCache(): 'allowed' | 'denied' | null {
  try {
    const raw = sessionStorage.getItem(ACCESS_CACHE_KEY);
    if (!raw) return null;
    const { state, ts } = JSON.parse(raw);
    // Session cache valid for 10 minutes
    if (Date.now() - ts > 10 * 60 * 1000) return null;
    return state as 'allowed' | 'denied';
  } catch {
    return null;
  }
}

function writeAccessCache(state: 'allowed' | 'denied') {
  try {
    sessionStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify({ state, ts: Date.now() }));
  } catch {}
}

const SalesAccessWrapper: React.FC = () => {
  // Read from sessionStorage first so page refresh shows correct state instantly
  const [accessState, setAccessState] = useState<'allowed' | 'denied'>(
    () => readAccessCache() ?? 'allowed'
  );
  const checked = React.useRef(false);

  useEffect(() => {
    if (checked.current) return; // StrictMode guard
    checked.current = true;

    getSalesProfile()
      .then(() => {
        writeAccessCache('allowed');
        setAccessState('allowed');
      })
      .catch((error) => {
        // Only deny for explicit NO_UNIT_ASSIGNED — transient errors (API_ERROR,
        // UNKNOWN, network failures) keep the current state so the user isn't
        // kicked out due to a flaky backend.
        if (error instanceof SalesError && error.code === 'NO_UNIT_ASSIGNED') {
          writeAccessCache('denied');
          setAccessState('denied');
        }
        // Any other error: silently keep current state
      });
  }, []);

  if (accessState === 'denied') {
    return (
      <NoUnitAssigned
        moduleName="Sales Unit"
        icon={ShoppingCart}
        theme={{
          gradient: 'from-emerald-50 via-green-50 to-teal-50',
          cardBorder: 'border-emerald-200',
          iconBg: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
        }}
      />
    );
  }

  return <SalesModule />;
};

export default SalesAccessWrapper;
